import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Users, Plus, Copy, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { formatMoney } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";
import { AjoCoOwnerModule, CoOwnerInvites } from "@/components/AjoCoOwnerModule";
import { toast } from "sonner";

export default function GroupsPage() {
  const { user } = useAuth();
  const cur = user?.preferred_currency || "GBP";
  const { groupId } = useParams();
  if (groupId) return <GroupDetail id={groupId} cur={cur} />;
  return <GroupsList cur={cur} />;
}

function GroupsList({ cur }) {
  const [groups, setGroups] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [form, setForm] = useState({ name: "", amount: 100, frequency: "monthly", start_date: new Date().toISOString().slice(0,10), insurance_enabled: false, insurance_fee_per_cycle: 1 });
  const nav = useNavigate();

  const load = async () => { const { data } = await api.get("/groups/mine"); setGroups(data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/groups", { ...form, amount: Number(form.amount), insurance_fee_per_cycle: form.insurance_enabled ? Number(form.insurance_fee_per_cycle) : 0 });
      toast.success(`Group created — code: ${data.code}`);
      setNewOpen(false); load();
      if (data.whatsapp_invite_link) {
        toast("Invite members instantly", {
          description: "Open a pre-filled WhatsApp message with the join code + first payment details.",
          action: { label: "Invite on WhatsApp", onClick: () => window.open(data.whatsapp_invite_link, "_blank") },
          duration: 15000,
        });
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const join = async () => {
    try {
      const { data } = await api.post("/groups/join", { code: joinCode.trim().toUpperCase() });
      toast.success(`Joined group — position ${data.position}`);
      setJoinOpen(false); setJoinCode(""); nav(`/groups/${data.group_id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not join"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ajo Groups</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Your Ajo groups</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setJoinOpen(true)} data-testid="groups-join-btn">Join with code</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="groups-new-btn"><Plus className="mr-1 h-4 w-4" /> New group</Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
        {groups.length === 0 ? (
          <div className="col-span-2 rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            <Users className="mx-auto h-8 w-8 opacity-40" />
            <p className="mt-3">No groups yet. Create one or join with a code.</p>
          </div>
        ) : groups.map((g) => (
          <Link key={g.group_id} to={`/groups/${g.group_id}`} className="rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/60" data-testid={`group-${g.group_id}`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg">{g.name}</h3>
              <span className="rounded-full bg-secondary px-2 py-0.5 font-mono text-xs">{g.code}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground capitalize">{g.frequency} · {g.members.length} members · {formatMoney(g.amount, cur)}/cycle</p>
            {g.insurance_enabled && <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent"><ShieldCheck className="h-3 w-3" /> Insured</span>}
          </Link>
        ))}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="group-create-dialog">
          <DialogHeader><DialogTitle>New Ajo group</DialogTitle><DialogDescription>Invite members with the auto-generated code after creation.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11" data-testid="group-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Amount ({cur})</Label><Input type="number" step="0.01" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="h-11" data-testid="group-amount" /></div>
              <div className="space-y-1.5"><Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="biweekly">Biweekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11" /></div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.insurance_enabled} onCheckedChange={(v) => setForm({ ...form, insurance_enabled: !!v })} data-testid="group-insurance" />
              Enable insurance pool (small fee covers defaulters)
            </label>
            {form.insurance_enabled && <div className="space-y-1.5"><Label>Insurance fee per cycle ({cur})</Label><Input type="number" step="0.01" min="0.1" value={form.insurance_fee_per_cycle} onChange={(e) => setForm({ ...form, insurance_fee_per_cycle: e.target.value })} className="h-11" /></div>}
            <Button type="submit" className="h-11 w-full" data-testid="group-create-submit">Create group</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent data-testid="group-join-dialog">
          <DialogHeader><DialogTitle>Join a group</DialogTitle><DialogDescription>Enter the 6-character code from the group owner.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="ABCDEF" className="h-11 font-mono" data-testid="group-join-code" />
            <Button onClick={join} className="h-11 w-full" data-testid="group-join-submit">Join</Button>
          </div>
        </DialogContent>
      </Dialog>
      <CoOwnerInvites />
    </div>
  );
}

function GroupDetail({ id, cur }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [contribAmt, setContribAmt] = useState("");

  const load = async () => { try { const { data } = await api.get(`/groups/${id}`); setData(data); } catch { setData({ error: true }); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const copy = async () => { await navigator.clipboard.writeText(data.group.code); toast.success("Code copied"); };
  const contribute = async () => {
    try {
      await api.post(`/groups/${id}/contribute`, { amount: Number(contribAmt) });
      toast.success("Contribution recorded");
      setContribAmt(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (data.error) return <div className="p-8 text-muted-foreground">Group not found.</div>;
  const g = data.group;
  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <Link to="/groups" className="text-sm text-muted-foreground hover:text-foreground">← Back to groups</Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Group</p>
          <h1 className="mt-1 font-display text-3xl font-medium">{g.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground capitalize">{g.frequency} · {formatMoney(g.amount, cur)}/cycle · starts {g.start_date}</p>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Code</span>
          <span className="font-mono font-medium" data-testid="group-code">{g.code}</span>
          <Button variant="ghost" size="icon" onClick={copy}><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {g.insurance_enabled && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-accent/30 bg-accent/5 p-3 text-sm">
          <ShieldCheck className="h-4 w-4 text-accent" /> Insurance pool: {formatMoney(g.insurance_pool_balance || 0, cur)} (covers defaulters)
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display text-lg">Payout schedule</h2>
          <div className="mt-4 divide-y divide-border rounded-xl border border-border">
            {data.schedule.map((row) => (
              <div key={row.position} className="flex items-center justify-between px-3 py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">{row.position}</span>
                  <span>{row.member}</span>
                </div>
                <div className="text-right">
                  <div className="font-medium">{formatMoney(row.payout_amount, cur)}</div>
                  <div className="text-xs text-muted-foreground">{row.payout_date}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-lg">Record contribution</h2>
            <p className="mt-1 text-xs text-muted-foreground">Track your cycle payment. Paying less than {formatMoney(g.amount, cur)} counts as a default and reduces your trust score.</p>
            <div className="mt-4 flex gap-2">
              <Input type="number" step="0.01" min="0.01" value={contribAmt} onChange={(e) => setContribAmt(e.target.value)} placeholder={`Amount in ${cur}`} className="h-11" data-testid="contribute-amount" />
              <Button onClick={contribute} disabled={!contribAmt} className="h-11" data-testid="contribute-submit">Pay</Button>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-lg">Recent contributions</h2>
            {data.contributions.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No contributions yet.</p>
            ) : (
              <div className="mt-4 divide-y divide-border">
                {data.contributions.slice(0, 10).map((c) => (
                  <div key={c.contribution_id} className="flex items-center justify-between py-2 text-sm">
                    <span>{c.user_name}{!c.on_time && <AlertTriangle className="ml-1 inline h-3 w-3 text-destructive" />}</span>
                    <span className="font-medium">{formatMoney(c.amount, cur)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <AjoCoOwnerModule groupId={id} isOwner={g.owner_user_id === user?.user_id} group={g} contributions={data.contributions} />
    </div>
  );
}
